import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import {
  FaArchway,
  FaPlus,
  FaSave,
  FaTrash,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaSearch,
  FaInfoCircle
} from "react-icons/fa";
import "../styles/ManageIdols.css";

const API = API_ENDPOINTS.IDOL;

const ManageIdols = () => {
  /* ================= HEADER ================= */
  const [headerTitle, setHeaderTitle] = useState("Temple Idols");
  const [headerDesc, setHeaderDesc] = useState(
    "Divine forms that embody blessings, protection, and spiritual energy."
  );

  /* ================= IDOLS ================= */
  const [idols, setIdols] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Redesign Tab & Search states
  const [activeTab, setActiveTab] = useState("cards"); // "cards" | "header"
  const [searchQuery, setSearchQuery] = useState("");

  /* ================= FETCH DATA ================= */
  const fetchIdols = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API);
      setIdols(res.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIdols();
  }, []);

  /* ================= UPDATE IDOL ================= */
  const updateIdol = (index, field, value) => {
    const updated = [...idols];
    updated[index][field] = value;
    setIdols(updated);
  };

  /* ================= IMAGE UPLOAD ================= */
  const handleImageUpload = (index, file) => {
    if (!file) return;

    const updated = [...idols];
    updated[index].image = file;
    updated[index].preview = URL.createObjectURL(file);
    setIdols(updated);
  };

  /* ================= ADD IDOL ================= */
  const addIdol = () => {
    setIdols([
      {
        id: Date.now(),
        title: "",
        image: null,
        preview: "",
      },
      ...idols,
    ]);
    setActiveTab("cards");
  };

  /* ================= DELETE IDOL ================= */
  const deleteIdol = async (idol, index) => {
    if (window.confirm("Are you sure you want to delete this idol profile?")) {
      try {
        if (idol._id) {
          await axios.delete(`${API}/${idol._id}`);
        }
        setIdols(idols.filter((_, i) => i !== index));
      } catch (err) {
        console.log(err);
      }
    }
  };

  /* ================= SAVE DATA ================= */
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);

      for (const idol of idols) {
        const formData = new FormData();
        formData.append("title", idol.title);

        if (idol.image instanceof File) {
          formData.append("image", idol.image);
        }

        if (!idol._id) {
          await axios.post(API, formData);
        } else {
          await axios.put(`${API}/${idol._id}`, formData);
        }
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 Temple Idols Updated Successfully!");
      fetchIdols();
    } catch (err) {
      console.log(err);
      alert("❌ Error saving idols");
    } finally {
      setSaving(false);
    }
  };

  /* ================= RENDER LOADING ================= */
  if (loading) {
    return (
      <div className="idols-loading">
        <div className="spinner"></div>
        <h2>Loading Idols Registry...</h2>
        <p>Connecting to backend API</p>
      </div>
    );
  }

  /* ================= FILTER LOGIC ================= */
  const filteredIdols = idols.filter(idol => 
    idol.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="manage-idols-container">
      {/* BANNER SECTION */}
      <div className="idols-banner">
        <div className="banner-content">
          <h1>🛕 Manage Temple Idols</h1>
          <p>Add, edit, or remove the details and portraits of deities in the temple sanctums.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaArchway className="icon" />
            <span><strong>{idols.length}</strong> Deities Listed</span>
          </div>
        </div>
      </div>

      {/* DASHBOARD CONTROLS & TAB WRAPPER */}
      <div className="dashboard-controls">
        <div className="tabs-wrapper">
          <button 
            className={`tab-btn ${activeTab === "cards" ? "active" : ""}`}
            onClick={() => setActiveTab("cards")}
          >
            <FaArchway /> Deity Profiles
          </button>
          <button 
            className={`tab-btn ${activeTab === "header" ? "active" : ""}`}
            onClick={() => setActiveTab("header")}
          >
            <FaInfoCircle /> Page Header
          </button>
        </div>

        {activeTab === "cards" && (
          <div className="search-filter-box">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search deities by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="quick-action-buttons">
          {activeTab === "cards" && (
            <button className="action-btn add-btn-primary" onClick={addIdol}>
              <FaPlus /> Add Deity
            </button>
          )}
          
          <button 
            className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* PANEL CONTAINER */}
      <div className="panel-container">
        
        {/* PANEL: PAGE HEADER */}
        {activeTab === "header" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaInfoCircle className="title-icon" /> Deity Directory Header</h3>
              <p>Configure the header texts that appear at the top of the idols web page.</p>
            </div>
            
            <div className="panel-card-body">
              <div className="form-group">
                <label>Header Title</label>
                <input
                  type="text"
                  value={headerTitle}
                  onChange={(e) => setHeaderTitle(e.target.value)}
                  placeholder="e.g. Divine Idols"
                />
              </div>

              <div className="form-group">
                <label>Header Description</label>
                <textarea
                  value={headerDesc}
                  onChange={(e) => setHeaderDesc(e.target.value)}
                  placeholder="Describe the spiritual significance of idols..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        )}

        {/* PANEL: IDOL CARDS */}
        {activeTab === "cards" && (
          <div className="directory-section fade-in">
            {filteredIdols.length === 0 ? (
              <div className="empty-state">
                <FaArchway className="empty-icon" />
                <h3>No Deities Found</h3>
                <p>{searchQuery ? "Try searching for a different name." : "Add a new deity card to publish on the website."}</p>
                <button className="action-btn add-btn-primary" onClick={addIdol}>
                  <FaPlus /> Add Deity Now
                </button>
              </div>
            ) : (
              <div className="idols-layout-grid">
                {filteredIdols.map((idol, index) => (
                  <div className="idol-entry-card" key={idol._id || idol.id}>
                    <div className="idol-card-preview-container">
                      {idol.preview ? (
                        <img src={idol.preview} alt="Deity Profile Preview" className="idol-preview-img" />
                      ) : idol.image ? (
                        <img
                          src={`${API_ENDPOINTS.BASE_URL}${idol.image}`}
                          alt="Deity Profile"
                          className="idol-preview-img"
                        />
                      ) : (
                        <div className="no-idol-placeholder">
                          <FaArchway className="icon" />
                          <span>No Image Uploaded</span>
                        </div>
                      )}
                      
                      <button className="idol-delete-icon-btn" onClick={() => deleteIdol(idol, index)}>
                        <FaTrash />
                      </button>
                    </div>

                    <div className="idol-card-form">
                      <div className="form-group-compact">
                        <label>Deity Name / Title</label>
                        <input
                          type="text"
                          value={idol.title || ""}
                          onChange={(e) => updateIdol(index, "title", e.target.value)}
                          placeholder="e.g. Lord Sri Ganesha"
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Deity Photo</label>
                        <label className="custom-file-upload">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(index, e.target.files[0])}
                          />
                          <FaCloudUploadAlt className="icon" />
                          <span>Choose Photo</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* FLOATING SAVE BAR */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes to deity directory will be lost.</span>
        </div>
        <button 
          className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="spinner-small"></div> Saving...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageIdols;
